import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trash2, Mail, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { PageSEO } from "../components/PageSEO";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = "contact@doctodays.com";

export default function DeleteAccountPage() {
  const [form, setForm] = useState({ email: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.email.includes("@")) {
      setError("Merci de saisir une adresse e-mail valide.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/account/deletion-request`, form);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue. Merci de réessayer ou de nous contacter par e-mail.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-slate-50 py-16 px-4">
        <PageSEO
          title="Suppression du compte demandée"
          description="Votre demande de suppression de compte DoctoDays a bien été enregistrée."
          canonical="https://doctodays.com/delete-account"
          noIndex
        />
        <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm p-8 text-center">
          <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Demande enregistrée</h1>
          <p className="text-slate-600 mb-6">
            Nous avons bien reçu votre demande de suppression de compte. Vos données seront supprimées
            sous <strong>30 jours ouvrés</strong>. Vous recevrez un e-mail de confirmation à la fin
            du processus.
          </p>
          <Link
            to="/"
            className="inline-block px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <PageSEO
        title="Suppression du compte"
        description="Demander la suppression de votre compte DoctoDays et de toutes les données associées, conformément au RGPD."
        canonical="https://doctodays.com/delete-account"
      />

      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Supprimer mon compte</h1>
              <p className="text-slate-500 text-sm">DoctoDays — Droit à l'effacement (RGPD)</p>
            </div>
          </div>

          {/* What gets deleted */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-red-900 mb-2">Données supprimées définitivement</h2>
                <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                  <li>Votre compte et vos identifiants de connexion</li>
                  <li>Votre nom, e-mail, téléphone et profil</li>
                  <li>Tous vos rendez-vous (passés et à venir)</li>
                  <li>L'intégralité de vos messages avec les praticiens</li>
                  <li>Vos avis et notations</li>
                  <li>Votre photo de profil (si praticien)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* What is kept for legal compliance */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-amber-900 mb-2">Données conservées (obligations légales)</h2>
                <p className="text-sm text-amber-800">
                  Les factures et documents comptables liés aux abonnements praticiens sont
                  conservés pendant <strong>10 ans</strong> conformément à la législation française
                  (Code de commerce). Ces données ne sont pas accessibles dans l'application.
                </p>
              </div>
            </div>
          </div>

          <h2 className="font-semibold text-slate-900 mb-3">Demander la suppression</h2>
          <p className="text-slate-600 text-sm mb-4">
            Renseignez ci-dessous l'adresse e-mail associée à votre compte DoctoDays.
            Pour des raisons de sécurité, nous vous contacterons à cette adresse pour
            confirmer votre identité avant de procéder à la suppression.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Adresse e-mail du compte <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="votre@email.com"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none"
                data-testid="delete-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Motif (facultatif)
              </label>
              <textarea
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="Aidez-nous à nous améliorer : pourquoi souhaitez-vous supprimer votre compte ?"
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none resize-none"
                data-testid="delete-reason-input"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-xl font-semibold transition"
              data-testid="delete-submit-btn"
            >
              {submitting ? "Envoi en cours…" : "Demander la suppression de mon compte"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-100 text-center">
            <p className="text-sm text-slate-500">
              Vous pouvez aussi nous écrire directement à
              {" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=Demande%20de%20suppression%20de%20compte`}
                className="text-emerald-600 hover:underline font-medium inline-flex items-center gap-1"
              >
                <Mail className="w-4 h-4" />
                {SUPPORT_EMAIL}
              </a>
            </p>
            <p className="text-xs text-slate-400 mt-3">
              Délai de traitement : <strong>30 jours ouvrés maximum</strong> · Aucune reconnexion possible après suppression.
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-slate-500 hover:text-emerald-600">
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
}
